var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { injectable } from '../../../Data/Decorators/Injectable';
import { inject } from '../../../Data/Decorators/Inject';
import { ICoordinatorType } from '../../Interfaces/ICoordinator';
import { ImportErrors } from '../../DTOs/Application/ImportErrors';
import { IPushNotificationsRepositoryType } from '../../Interfaces/IPushNotificationsRepository';
import { IConfigurationRepositoryType } from '../../UseCases/Configuration/IConfigurationRepository';
import { IImagesRepositoryType } from '../../Interfaces/IImagesRepository';
import { IApplicationRepositoryType } from '../../UseCases/Application/IApplicationRepository';
import { IUserApplicationRepositoryType } from '../../UseCases/UserApplication/IUserApplicationRepository';
import { PushNotificationAction } from '../../DTOs/PushNotifications/PushNotificationDTO';
import { ILocalizationRepositoryType } from '../../UseCases/Localization/ILocalizationRepository';
import * as ImagePicker from 'react-native-image-picker';
import { AppState, Platform } from 'react-native';
import { waitTimeout, whenAppIsActive } from '../../../Helpers/Background/Background';
let CoordinatorService = class CoordinatorService {
    constructor(coordinator, configurationRepository, localizationRepository, applicationRepository, userApplicationRepository, imagesRepository, pushNotificationsRepository) {
        this.coordinator = coordinator;
        this.configurationRepository = configurationRepository;
        this.localizationRepository = localizationRepository;
        this.applicationRepository = applicationRepository;
        this.userApplicationRepository = userApplicationRepository;
        this.imagesRepository = imagesRepository;
        this.pushNotificationsRepository = pushNotificationsRepository;
    }
    getIsShowedNotificationsPermissions() {
        return this.coordinator.getIsShowedNotificationsPermissions();
    }
    getCurrentRouteParams() {
        return this.coordinator.getCurrentRouteParams();
    }
    dismissAppUpdate() {
        return this.coordinator.dismissAppUpdate();
    }
    init() {
        if (Platform.OS === 'web') {
            return Promise.resolve();
        }
        this.applicationRepository.getNewAppVersion().observe(this, value => {
            if (value && value.build > this.userApplicationRepository.getApplicationBuild()) {
                setTimeout(() => this.navigateToAppUpdate(), 1000);
            }
        });
        return Promise.resolve();
    }
    navigateToAppUpdate() {
        return this.coordinator.navigateToAppUpdate();
    }
    dismissShare() {
        this.dismiss();
        if (this._resolveShareOption) {
            this._resolveShareOption();
        }
        this._rejectShareOption = undefined;
        this._resolveShareOption = undefined;
    }
    resolveShareOption(option) {
        if (!this._resolveShareOption) {
            return Promise.reject(new Error('No share option resolve handler!'));
        }
        this.dismiss();
        return Promise.resolve()
            .then(() => { var _a; return (_a = this._resolveShareOption) === null || _a === void 0 ? void 0 : _a.call(this, option); })
            .then(() => option);
    }
    rejectShareOption(error) {
        if (!this._rejectShareOption) {
            return Promise.reject(new Error('No share option reject handler!'));
        }
        this.dismiss();
        return Promise.resolve()
            .then(() => { var _a; return (_a = this._rejectShareOption) === null || _a === void 0 ? void 0 : _a.call(this, new Error(error)); })
            .then(() => undefined);
    }
    showShareOptions(params) {
        return new Promise((res, rej) => {
            this.coordinator.showShareOptions(params)
                .then(() => {
                this._resolveShareOption = res;
                this._rejectShareOption = rej;
            })
                .catch(rej);
        });
    }
    setOnNavigateToDeepLink(fn) {
        this._onDeepLink = fn;
    }
    navigateToDeepLink(deepLink) {
        if (!this._onDeepLink) {
            return Promise.reject(new Error('No deep link handler!'));
        }
        return Promise.resolve()
            .then(() => { var _a; return (_a = this._onDeepLink) === null || _a === void 0 ? void 0 : _a.call(this, deepLink); })
            .then(() => this.coordinator.getCurrentRoute());
    }
    showNotificationsPermissions(onSuccess, onDismiss, forceReset) {
        this._onSuccessNotificationPermissions = onSuccess;
        this._onDismissNotificationPermissions = onDismiss;
        const assets = this.configurationRepository.getNotificationPermissionsAssets();
        const timeoutTime = this.configurationRepository.getPreloadImageTimeout();
        return new Promise(res => {
            let timeout = setTimeout(() => {
                timeout = undefined;
                res(null);
            }, timeoutTime * 1000);
            this.imagesRepository.downloadImages(assets, { preload: true })
                .then(() => {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = undefined;
                    res(null);
                }
            })
                .catch(error => {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = undefined;
                    res(null);
                }
            });
        })
            .then(() => this.coordinator.showNotificationsPermissions(forceReset));
    }
    hideNotificationsPermissions(isSuccessful) {
        if (isSuccessful && this._onSuccessNotificationPermissions) {
            return Promise.resolve()
                .then(() => { var _a; return (_a = this._onSuccessNotificationPermissions) === null || _a === void 0 ? void 0 : _a.call(this); })
                .then(() => this.resetNotificationsPermissionsHandlers())
                .then(() => this.coordinator.getCurrentRoute());
        }
        if (!isSuccessful && this._onDismissNotificationPermissions) {
            return Promise.resolve()
                .then(() => { var _a; return (_a = this._onDismissNotificationPermissions) === null || _a === void 0 ? void 0 : _a.call(this); })
                .then(() => this.resetNotificationsPermissionsHandlers())
                .then(() => this.coordinator.getCurrentRoute());
        }
        this.resetNotificationsPermissionsHandlers();
        if (this.coordinator.getIsShowedNotificationsPermissions()) {
            this.dismiss();
        }
        return Promise.resolve(this.coordinator.getCurrentRoute());
    }
    resetNotificationsPermissionsHandlers() {
        this._onSuccessNotificationPermissions = undefined;
        this._onDismissNotificationPermissions = undefined;
    }
    navigateToPushNotification(id) {
        const timeout = AppState.currentState === 'active'
            ? 0
            : 500;
        return whenAppIsActive()
            .then(() => waitTimeout(timeout))
            .then(whenAppIsActive)
            .then(() => this.pushNotificationsRepository.getPushNotification(id))
            .then(notification => {
            var _a, _b, _c;
            const localization = this.configurationRepository.getPushNotificationLocalization();
            if ((_a = notification.value) === null || _a === void 0 ? void 0 : _a.valid_to) {
                const date = Date.create().getUnix();
                if (date > notification.value.valid_to) {
                    return this.showError({ description: localization.the_notification_has_been_expired });
                }
            }
            if (!((_b = notification.value) === null || _b === void 0 ? void 0 : _b.action) || ((_c = notification.value) === null || _c === void 0 ? void 0 : _c.action) === PushNotificationAction.navigate_to_notification) {
                return this.coordinator.navigateToPushNotification(id);
            }
            if (notification.value.action === PushNotificationAction.navigate_to_deep_link) {
                if (!notification.value.deep_link) {
                    return this.showError({ description: localization.deep_link_is_missing_in_push_notification });
                }
                return this.navigateToDeepLink(notification.value.deep_link);
            }
            return this.coordinator.getCurrentRoute();
        });
    }
    dismissPushNotifications(id) {
        this.dismiss();
        return this.pushNotificationsRepository.deletePushNotification(id)
            .then(() => this.coordinator.getCurrentRoute());
    }
    showToast(toast) {
        return this.coordinator.showToast(toast);
    }
    launchCamera(options) {
        return new Promise(res => setTimeout(res, 10))
            .then(() => ImagePicker.launchCamera(Object.assign({ mediaType: 'photo', quality: 1 }, options)))
            .then(result => {
            if (result.errorMessage) {
                return Promise.reject({ message: result.errorMessage, code: ImportErrors.noCameraPermissions });
            }
            if (result.didCancel) {
                return [];
            }
            return this.convertAssets(result.assets || []);
        });
    }
    launchGallery(options) {
        return new Promise(res => setTimeout(res, 10))
            .then(() => ImagePicker.launchImageLibrary(Object.assign({ mediaType: 'photo', quality: 1 }, options)))
            .then(result => {
            if (result.errorMessage) {
                return Promise.reject({ message: result.errorMessage, code: ImportErrors.noGalleryPermissions });
            }
            if (result.didCancel) {
                return [];
            }
            return this.convertAssets(result.assets || []);
        });
    }
    convertAssets(assets) {
        return assets.map(asset => ({
            uri: asset.uri,
            width: asset.width,
            height: asset.height,
            type: asset.type,
            duration: asset.duration
        }));
    }
    showError(params) {
        if ((params.interactions || []).length <= 0) {
            params.interactions = [
                { action: 'ok', type: 'primary', text: 'Ok', onPress: () => this.hideError() }
            ];
        }
        if (!params.title) {
            params.title = this.localizationRepository.getErrorPopUpLocalization().title;
        }
        return this.coordinator.showError(params);
    }
    showPopUp(params) {
        if (!params.interactions) {
            params.interactions = [
                { type: 'primary', action: 'ok', text: 'OK', onPress: () => this.hidePopUp() }
            ];
        }
        return this.coordinator.showPopUp(params);
    }
    showLoading() {
        return this.coordinator.showLoading();
    }
    hideError() {
        return this.coordinator.hideError();
    }
    hidePopUp() {
        return this.coordinator.hidePopUp();
    }
    hideLoading() {
        return this.coordinator.hideLoading();
    }
    dismiss() {
        return this.coordinator.dismiss();
    }
};
CoordinatorService = __decorate([
    injectable({
        type: 'singleton'
    }),
    __param(0, inject(ICoordinatorType)),
    __param(1, inject(IConfigurationRepositoryType)),
    __param(2, inject(ILocalizationRepositoryType)),
    __param(3, inject(IApplicationRepositoryType)),
    __param(4, inject(IUserApplicationRepositoryType)),
    __param(5, inject(IImagesRepositoryType)),
    __param(6, inject(IPushNotificationsRepositoryType)),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object, Object, Object])
], CoordinatorService);
export { CoordinatorService };
